import { dims, esc, pageNumberText, themeVars } from "./pages";

export function staticize(html, keepMarkup = false) {
  const t = document.createElement("div");
  t.innerHTML = html;
  t.querySelectorAll("select.dd-field").forEach((s) => {
    const sp = document.createElement("span");
    sp.className = "field-static";
    sp.textContent = s.options[s.selectedIndex]?.text || "";
    (s.closest(".field-wrap") || s).replaceWith(sp);
  });
  t.querySelectorAll("input.date-field").forEach((i) => {
    const v = i.getAttribute("value");
    const sp = document.createElement("span");
    sp.className = "field-static";
    sp.textContent = v
      ? new Date(v + "T00:00").toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
      : "__________";
    (i.closest(".field-wrap") || i).replaceWith(sp);
  });
  t.querySelectorAll("input.chk-field").forEach((i) => {
    const sp = document.createElement("span");
    sp.className = "chk-static";
    sp.textContent = i.hasAttribute("checked") ? "☑" : "☐";
    (i.closest(".field-wrap") || i).replaceWith(sp);
  });
  t.querySelectorAll(".logo-field:not(:has(img))").forEach((n) => n.remove());
  t.querySelectorAll(".img-selected").forEach((i) => i.classList.remove("img-selected"));
  t.querySelectorAll(".ov-ui, .pdf-marker").forEach((n) => n.remove());
  if (!keepMarkup) {
    t.querySelectorAll(".cmt, .tc-ins").forEach((n) => n.replaceWith(...n.childNodes));
    t.querySelectorAll(".tc-del").forEach((n) => n.remove());
  }
  t.querySelectorAll('.ov-text[contenteditable="true"]').forEach((n) => n.removeAttribute("contenteditable"));
  return t.innerHTML.replace(/\u200B/g, "");
}

function splitTable(table, maxH) {
  const rows = [...table.rows];
  const chunks = [[]];
  let used = 0;
  rows.forEach((r) => {
    const h = r.getBoundingClientRect().height;
    if (used + h > maxH && chunks[chunks.length - 1].length) { chunks.push([]); used = 0; }
    chunks[chunks.length - 1].push(r);
    used += h;
  });
  let ref = table;
  chunks.forEach((c) => {
    const t = table.cloneNode(false);
    const tb = document.createElement("tbody");
    c.forEach((r) => tb.appendChild(r));
    t.appendChild(tb);
    ref.after(t);
    ref = t;
  });
  table.remove();
}

const styleStr = (o) => Object.entries(o).map(([k, v]) => `${k}:${v}`).join(";");

export function paginate(html, s) {
  const d = dims(s);
  const host = document.createElement("div");
  host.className = "doc-content";
  host.style.cssText = `position:absolute;left:-30000px;top:0;width:${d.cw}px;tab-size:${s.tabSize}px;${styleStr(themeVars(s))}`;
  host.innerHTML = staticize(html);
  document.body.appendChild(host);
  [...host.childNodes].forEach((n) => {
    if (n.nodeType === 3) {
      if (!n.data.trim()) return n.remove();
      const p = document.createElement("p");
      n.replaceWith(p);
      p.appendChild(n);
    } else if (n.nodeType !== 1) n.remove();
  });
  [...host.children].forEach((el) => {
    if (el.tagName === "TABLE" && el.offsetHeight > d.ch) splitTable(el, d.ch);
  });
  const hostTop = host.getBoundingClientRect().top;
  const pages = [[]];
  let start = null;
  for (const el of [...host.children]) {
    if (el.classList.contains("page-break")) { pages.push([]); start = null; continue; }
    const r = el.getBoundingClientRect();
    const top = r.top - hostTop;
    const bottom = r.bottom - hostTop;
    if (start === null) start = top;
    if (bottom - start > d.ch + 1 && pages[pages.length - 1].length) { pages.push([]); start = top; }
    pages[pages.length - 1].push(el.outerHTML);
  }
  host.remove();
  return pages.map((p) => p.join(""));
}

export function sheetHTML(content, s, n, total) {
  const d = dims(s);
  const pn = s.pageNumbers || {};
  const [pv, ph] = (pn.position || "bottom-center").split("-");
  const pnHtml = pn.enabled ? `<div style="text-align:${ph}">${esc(pageNumberText(pn.format, n, total))}</div>` : "";
  const wm = s.watermark?.text
    ? `<div class="sheet-watermark" style="color:${s.watermark.color};opacity:${s.watermark.opacity}"><span style="font-size:${Math.round(d.w / 9)}px">${esc(s.watermark.text)}</span></div>`
    : "";
  return `<div class="sheet" style="width:${d.w}px;height:${d.h}px;${styleStr(themeVars(s))}">${wm}
<div class="sheet-header" style="height:${d.mt}px;left:${d.ml}px;right:${d.mr}px">${pv === "top" ? pnHtml : ""}<div class="doc-content hf-text">${s.header || ""}</div></div>
<div class="sheet-body doc-content" style="top:${d.mt}px;left:${d.ml}px;width:${d.cw}px;height:${d.ch}px;tab-size:${s.tabSize}px">${content}</div>
<div class="sheet-footer" style="height:${d.mb}px;left:${d.ml}px;right:${d.mr}px"><div class="doc-content hf-text">${s.footer || ""}</div>${pv === "bottom" ? pnHtml : ""}</div></div>`;
}

export function renderPages(html, s) {
  const pages = paginate(html, s);
  return pages.map((c, i) => sheetHTML(c, s, i + 1, pages.length));
}

export const waitImages = (el) =>
  Promise.all([...el.querySelectorAll("img")].map((i) => (i.complete ? null : new Promise((r) => { i.onload = r; i.onerror = r; }))));

export function printPages(pages, s) {
  const d = dims(s);
  let root = document.getElementById("print-root");
  if (!root) { root = document.createElement("div"); root.id = "print-root"; document.body.appendChild(root); }
  root.innerHTML = pages.join("");
  let st = document.getElementById("print-page-style");
  if (!st) { st = document.createElement("style"); st.id = "print-page-style"; document.head.appendChild(st); }
  st.textContent = `@page{size:${d.wmm}mm ${d.hmm}mm;margin:0}`;
  document.body.classList.add("printing");
  const done = () => {
    document.body.classList.remove("printing");
    root.innerHTML = "";
    window.removeEventListener("afterprint", done);
  };
  window.addEventListener("afterprint", done);
  waitImages(root).then(() => window.print());
}
